import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TournamentBracketTree from '../components/TournamentBracketTree';
import { getTournamentV2 } from '../services/tournamentsV2';
import { TournamentV2 } from '../types';
import './TournamentBracketFullscreen.css';

const TournamentBracketFullscreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tournament, setTournament] = useState<TournamentV2 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    
    const loadTournament = async () => {
      try {
        setLoading(true);
        const data = await getTournamentV2(parseInt(id));
        setTournament(data);
      } catch (err) {
        console.error('Error loading tournament:', err);
        setError(err instanceof Error ? err.message : 'Failed to load tournament');
      } finally {
        setLoading(false);
      }
    };

    loadTournament();
  }, [id]);

  const handleClose = () => {
    navigate(-1); // Go back to previous page
  };

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  if (loading) {
    return (
      <div className="fullscreen-loading">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className="fullscreen-error">
        <p className="error-message">{error || t('tournaments.notFound')}</p>
        <button onClick={handleClose} className="btn btn-primary">
          {t('common.back')}
        </button>
      </div>
    );
  }

  return (
    <div className="bracket-fullscreen-page">
      {/* Header */}
      <div className="fullscreen-header">
        <div className="header-content">
          <h1 className="tournament-title">{tournament.name}</h1>
          <div className="tournament-meta">
            <span className="tournament-format">
              {t(`tournaments.formats.${tournament.format}`)}
            </span>
            <span className="tournament-status">
              {t(`tournaments.status.${tournament.status}`)}
            </span>
          </div>
        </div>
        <div className="header-actions">
          <button 
            onClick={handleFullscreen}
            className="btn btn-secondary btn-icon"
            title={t('common.fullscreen')}
          >
            ⛶
          </button>
          <button onClick={handleClose} className="btn btn-primary">
            {t('common.close')}
          </button>
        </div>
      </div>

      {/* Bracket Tree with Auto-Refresh */}
      <div className="fullscreen-bracket-container">
        <TournamentBracketTree 
          tournamentId={parseInt(id!)} 
          autoRefresh={true}
          refreshInterval={5000}
        />
      </div>
    </div>
  );
};

export default TournamentBracketFullscreen;

